import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { Box } from '@jotta/ui/Box'
import { BrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { Flex } from '@jotta/ui/Flex'
import type { ThemeUIStyleObject } from '@jotta/ui/themeui'
import { SpinnerThemeUI, TextThemeUI } from '@jotta/ui/themeui'
import { getBrandZIndex } from '@jotta/ui/zIndex'
import { useThrottle } from '@jotta/hooks'
import type { PhotosApi } from '@jotta/types/PhotosApi'
import { t } from '@lingui/macro'
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion'
import { observer } from 'mobx-react-lite'
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type {
  CommentResponseWithImage,
  CommentWithImage,
} from '../../store/CommentQuery'
import { CommentInput } from './CommentInput'
import { CommentItem } from './CommentItem'
import { CommentItemStore, CommentsContext } from './CommentsContext'
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog'
import { TextareaStore } from './TextArea'
import SvgX from '@jotta/ui/SvgX'

const CommentSectionHeader: React.FC<{
  darkMode: boolean
  onClose?: () => void
}> = ({ darkMode, onClose }) => {
  return (
    <Flex
      sx={{
        height: 'var(--header-height)',
        borderBottom: '1px solid',
        borderBottomColor: darkMode ? '#191919' : 'border',
        bg: darkMode ? 'photoInfoBgColor' : 'white',
        zIndex: getBrandZIndex('dropdown'),
        alignItems: 'center',
      }}
    >
      <Button
        onClick={onClose}
        sx={{
          background: 'none',
          border: 'none',
          px: 3,
          opacity: 0.6,
          height: '100%',
          color: 'inherit',
          borderRadius: 2,
          '&:hover': {
            opacity: 0.9,
          },
          '&:focus-visible': {
            boxShadow: 'inset 0 0 0 2px var(--color-focus)',
          },
          span: {
            display: 'flex',
          },
        }}
      >
        <BrandIcon icon={SvgX} sx={{ fontSize: 4 }} />
      </Button>
      <TextThemeUI
        as="span"
        sx={{
          lineHeight: '60px',
          fontSize: 3,
          verticalAlign: 'top',
          color: 'inherit',
        }}
      >
        {t`Comments.title`}
      </TextThemeUI>
    </Flex>
  )
}

const EditOverlay: React.FC<{ show?: boolean }> = ({ show = false }) => {
  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            sx={{
              pointerEvents: 'none',
              backgroundColor: 'black',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        )}
      </AnimatePresence>
    </>
  )
}

const CommentSpinner: React.FC<{ show?: boolean }> = ({ show = false }) =>
  show ? (
    <Box sx={{ margin: 'auto' }}>
      <SpinnerThemeUI sx={{ mt: 3, display: 'block' }} />
    </Box>
  ) : null

const CommentList: React.FC<{
  show?: boolean | number
  comments: CommentWithImage[]
  textStore: TextareaStore
  album?: PhotosApi.Album
  darkMode: boolean
}> = ({ show = false, comments, textStore, album, darkMode }) => {
  if (!show) {
    return null
  }

  if (!comments.length) {
    return <span sx={{ opacity: 0.6, margin: 4 }}>{t`Comments.emptyList`}</span>
  }

  return (
    <LayoutGroup>
      <AnimatePresence initial={false}>
        {comments.map(comment => (
          // <motion.div
          //   key={comment.id}
          //   initial={{ opacity: 0, height: 'auto' }}
          //   animate={{ opacity: 1, height: 'auto' }}
          //   exit={{ opacity: 0, height: 0 }}
          //   transition={{
          //     duration: 0.2,
          //     height: {
          //       delay: 0.2,
          //     },
          //   }}
          // >
          <CommentItem
            textStore={textStore}
            album={album}
            {...comment}
            key={comment.id}
            darkMode={darkMode}
          />
          // </motion.div>
        ))}
      </AnimatePresence>
    </LayoutGroup>
  )
}

// Start list scrolled to bottom and keep position if scrollTop == 0 or at bottom
function useScrollPreserve<E extends HTMLElement>(
  ref: React.RefObject<E>,
  observed: unknown,
) {
  const prevScrollHeight = useRef(0)
  const scrollFromBottom = useRef(0)

  scrollFromBottom.current = ref.current
    ? ref.current.scrollHeight -
      ref.current.clientHeight -
      ref.current.scrollTop
    : 0

  useEffect(() => {
    if (!ref.current) {
      return
    }

    if (prevScrollHeight.current !== ref.current.scrollHeight) {
      // If scrolled to bottom when new contend added, keep it scrolled to bottom
      if (scrollFromBottom.current === 0) {
        ref.current.scrollTop =
          ref.current.scrollHeight - ref.current.clientHeight
      }
      // If scrolled to top when new content was added preserve scroll position
      else if (ref.current.scrollTop === 0) {
        ref.current.scrollTop =
          ref.current.scrollHeight - prevScrollHeight.current
      }
      prevScrollHeight.current = ref.current.scrollHeight
    }
  }, [prevScrollHeight, ref, observed])
}

const CommentSection = observer<
  {
    pages: CommentResponseWithImage[] | undefined
    hasNextPage: boolean
    onClose?: (open?: boolean) => void
    onAddComment?: (text: string) => void
    onEdit?: (contextKey: string, commentId: string, text: string) => void
    onReply?: (contextKey: string, commentId: string, text: string) => void
    onDelete?: (contextKey: string, commentId: string) => void
    onFetchNextPage?: () => void
    darkMode?: boolean
    isFetchingNextPage: boolean
    scrollToCommentId?: string
    album?: PhotosApi.Album
  },
  HTMLDivElement
>(
  forwardRef(function CommentSection(
    {
      pages = [],
      onFetchNextPage,
      hasNextPage,
      onClose,
      onAddComment,
      onEdit,
      onReply,
      onDelete,
      darkMode = false,
      isFetchingNextPage,
      scrollToCommentId,
      album,
    },
    ref,
  ) {
    type CommentInfo = { commentId: string; contextKey: string }

    const listRef = useRef<HTMLDivElement>(null)
    const [editMode, setEditMode] = useState<CommentInfo | null>(null)
    const [confirmDelete, setConfirmDelete] = useState<CommentInfo | null>(null)
    const textStore = useMemo(() => new TextareaStore(''), [])
    const authenticated = useAuthenticated()

    useScrollPreserve(listRef, pages)

    const loadMore = useCallback(() => {
      if (
        !onFetchNextPage ||
        !hasNextPage ||
        isFetchingNextPage ||
        !listRef.current
      ) {
        return
      }

      const el = listRef.current

      if (el.scrollTop <= 400) {
        onFetchNextPage()
      }
    }, [onFetchNextPage, hasNextPage, isFetchingNextPage])

    // Load more whenever function (its deps) changes
    useEffect(() => {
      loadMore()
    }, [loadMore])

    const handleScroll = useThrottle(loadMore, 100)

    const onSubmit = (text: string) => {
      if (!text) {
        return
      }
      if (editMode) {
        onEdit && onEdit(editMode.contextKey, editMode.commentId, text.trim())
        setEditMode(null)
      } else {
        if (listRef.current) {
          // Scroll to bottom
          const el = listRef.current
          el.scrollTop = el.scrollHeight - el.clientHeight
        }
        onAddComment && onAddComment(text.trim())
      }
    }

    const normalStyle: ThemeUIStyleObject = {
      height: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
      wordBreak: 'break-word',
      display: 'flex',
      flexDirection: 'column',
    }

    const editStyle: ThemeUIStyleObject = {
      ...{ normalStyle },
      pointerEvents: 'none',
      userSelect: 'none',
    }

    const enableEditMode = (
      contextKey: string,
      commentId: string,
      text: string,
    ) => {
      setEditMode({ contextKey, commentId })
      textStore.text = text
    }

    const doConfirmDelete = () => {
      if (!confirmDelete) {
        return
      }
      if (onDelete) {
        onDelete(confirmDelete.contextKey, confirmDelete.commentId)
      }
      setConfirmDelete(null)
    }

    const comments = useMemo(
      () =>
        pages
          .map(page => page.commentsList)
          .reverse()
          .flat(),
      [pages],
    )

    const commentItemStore = useMemo(() => new CommentItemStore(), [])

    useEffect(() => {
      if (!editMode) {
        return
      }

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setEditMode(null)
          e.preventDefault()
        }
      }

      document.addEventListener('keydown', handleEscape, true)

      return () => {
        document.removeEventListener('keydown', handleEscape, true)
      }
    }, [editMode])

    return (
      <CommentsContext.Provider
        value={{
          darkMode,
          scrollToCommentId,
          onEdit: enableEditMode,
          onDelete(contextKey, commentId) {
            setConfirmDelete({ contextKey, commentId })
          },
          onReply(contextKey, commentId, text) {
            if (onReply) {
              onReply(contextKey, commentId, text)
            }
          },
          commentItemStore,
        }}
      >
        <Box
          ref={ref}
          sx={{
            flex: '1 1 auto',
            height: '100%',
            backgroundColor: darkMode ? 'photoInfoBgColor' : 'white',
            color: darkMode ? 'white' : '#333', // TODO: Fix hardcoded color
            ...(darkMode
              ? { '--_color-focus': 'var(--color-focus-dark)' }
              : {}),
          }}
        >
          <Flex column sx={{ height: '100%' }}>
            <CommentSectionHeader onClose={onClose} darkMode={darkMode} />
            <Box
              sx={{
                flex: '1 1 auto',
                //position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                ref={listRef}
                sx={editMode ? editStyle : normalStyle}
                onScroll={handleScroll}
              >
                <CommentList
                  show={!isFetchingNextPage || comments.length}
                  textStore={textStore}
                  comments={comments}
                  album={album}
                  darkMode={darkMode}
                />
                <CommentSpinner show={isFetchingNextPage} />
              </Box>
              <EditOverlay show={Boolean(editMode)} />
            </Box>
            <CommentInput
              textStore={textStore}
              onSubmit={onSubmit}
              notLoginUser={authenticated === 'FALSE'}
              darkMode={darkMode}
            />
          </Flex>

          <ConfirmDeleteDialog
            onClose={() => {
              setConfirmDelete(null)
            }}
            onConfirm={doConfirmDelete}
            open={Boolean(confirmDelete)}
          />
        </Box>
      </CommentsContext.Provider>
    )
  }),
)

export default CommentSection
